import { useState } from "react";
import { SearchBar } from "../Search/SearchBar";
import { Button } from "../ui/button";

import {
  Carousel,
  CarouselContent,
  CarouselIndicator,
  CarouselItem,
} from "../ui/carousel";
import { SearchParams } from "~/types/search";
import {
  useNumberOfMatchingLocations,
  useTotalNumberOfLocations,
} from "~/lib/tanstack-query/hooks/useLocations";
import { Link, useNavigate } from "@tanstack/react-router";
import { AmountFormatted } from "../AmountFormatted";
import { LocationWithImages } from "~/lib/server-functions/get-locations";
import { getLocationPath } from "~/lib/utils/location-path";
import { withLocale } from "~/lib/utils/with-locale";
import noImageFound from "~/assets/no-image.svg";
import Autoplay from "embla-carousel-autoplay";
import { LocationPlanBadge } from "../LocationPlanBadge";
import { buildImageUrl } from "~/lib/utils";

export const Hero = ({
  header,
  aston,
  astonButtonLabel,
  locations,
  tagline,
}: {
  header: string;
  aston: string;
  astonButtonLabel: string;
  locations: LocationWithImages[];
  tagline: string;
}) => {
  const [searchParams, setSearchParams] = useState<SearchParams>(() => ({
    location: [],
    placeIds: undefined,
    guests: "",
    type: null,
    filters: undefined,
    sort: "recommended",
    rating: 0,
    searchRadius: undefined,
    locationIdOrName: undefined,
    occasion: undefined,
    loc: undefined,
    locationType: undefined,
  }));
  const { data: totalNumberOfLocations } = useTotalNumberOfLocations();

  const {
    data: numberOfMatchingLocations,
    isPending: isPendingNumberOfLocations,
  } = useNumberOfMatchingLocations(searchParams);
  const navigate = useNavigate();

  const headerParts = header.split("{locationCount}");

  return (
    <div className="relative layout-place-full-width">
      <Carousel
        opts={{
          loop: true,
        }}
        plugins={[
          Autoplay({
            delay: 10000,
          }),
        ]}
      >
        <CarouselContent>
          {locations.map((location, index) => {
            return (
              <CarouselItem key={location.id + index}>
                <div className="relative h-[calc(100dvh-var(--top-panel-height)-var(--location-drawer-height))] md:h-[calc(100vh-var(--top-panel-height)-var(--hero-aston-height)-var(--location-drawer-height))] max-h-[calc(1080px-var(--top-panel-height)-var(--hero-aston-height)-var(--location-drawer-height))] flex items-center justify-center">
                  <img
                    // Find the first image where:
                    // - mimeType does not start with "video"
                    // - category does not end with "_logo" or "_contact_person"
                    // Fallback to the first image if none found, or render nothing if no images.
                    src={
                      (() => {
                        const image =
                          (location.images &&
                            location.images.find(
                              (img) =>
                                img.mimetype &&
                                !img.mimetype.startsWith("video") &&
                                img.purpose === "location_gallery_image",
                            )) ||
                          (location.images && location.images[0]);
                        return image?.path
                          ? buildImageUrl(image.path, {
                              size: "2545x936",
                            })
                          : noImageFound;
                      })()
                    }
                    alt=""
                    className="absolute top-0 left-0 w-full h-full object-cover z-0"
                  />
                  <div className="absolute top-0 left-0 w-full h-full bg-black/45 z-10"></div>
                  <Link
                    to={withLocale(getLocationPath(location))}
                    className="absolute bottom-0 right-0 text-white pb-8 md:pb-16 px-8 flex gap-6 z-20 max-w-[calc(100%)]"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-base md:text-lg truncate">
                        {location.name}
                      </p>
                      <p className="text-sm truncate">
                        {location.locationTypes
                          .map((type) => type.name)
                          .join(", ")}
                        &nbsp;in&nbsp;
                        {location.city}
                      </p>
                    </div>
                    <div className="shrink-0">
                      <LocationPlanBadge
                        plan={location.plan ?? ""}
                        className="size-15 object-contain"
                      />
                    </div>
                  </Link>
                </div>
              </CarouselItem>
            );
          })}
        </CarouselContent>
        <CarouselIndicator className="absolute bottom-[35px] left-1/2 -translate-x-1/2 z-10 hidden md:flex" />
      </Carousel>
      <div className="absolute z-10 top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 w-full flex flex-col items-center gap-6 px-6 pb-28 -mt-17 md:mt-0">
        <h2 className="text-white font-medium text-4xl text-center md:text-6xl order-1">
          {headerParts.length === 2 ? (
            <>
              {headerParts[0]}
              <span className="text-5xl md:text-6xl">
                <AmountFormatted countUp>
                  {totalNumberOfLocations ?? 0}
                </AmountFormatted>
              </span>
              {headerParts[1]}
            </>
          ) : (
            headerParts[0]
          )}
        </h2>
        <SearchBar
          className="order-3 md:order-2"
          size="large"
          location={searchParams.location}
          placeIds={searchParams.placeIds}
          guests={searchParams.guests}
          type={searchParams.type}
          setLocationSearch={(location, placeIds) =>
            setSearchParams((prev) => ({ ...prev, location, placeIds }))
          }
          setGuests={(guests) =>
            setSearchParams((prev) => ({ ...prev, guests }))
          }
          setType={(type) => setSearchParams((prev) => ({ ...prev, type }))}
          numberOfMatchingLocations={numberOfMatchingLocations || 0}
          isPending={isPendingNumberOfLocations}
          onSubmit={(data) => {
            navigate({
              to: withLocale("/search"),
              search: data,
            });
          }}
        />
        {tagline && (
          <p className="order-2 md:order-3 text-white md:text-2xl text-center">
            {tagline}
          </p>
        )}
      </div>
      <div className="sm:h-(--hero-aston-height) bg-gradient-brand-not-interactive px-6 py-4 sm:py-2 flex flex-col sm:flex-row items-start sm:items-center justify-center gap-3 sm:gap-6">
        <p
          dangerouslySetInnerHTML={{
            __html: aston
              .replaceAll("\r\n", "<br />")
              .replaceAll("\n", "<br />"),
          }}
        />
        <Button
          size={"lg"}
          variant={"outline"}
          className="hover:bg-transparent hover:scale-105 transition-transform"
        >
          <a
            href={`${import.meta.env.VITE_ADMIN_URL}${withLocale("/sign-up")}`}
          >
            {astonButtonLabel}
          </a>
        </Button>
      </div>
    </div>
  );
};
